import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ApiService from '../services/api';

// Custom hook for products
export const useProducts = (cooperativeId = null, page = 1, limit = 10) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getProducts(page, limit, cooperativeId);
      
      if (response.status === 200) {
        setProducts(response.data || []);
        setPagination(response.pagination || {});
      }
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [cooperativeId, page, limit]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refresh = () => {
    fetchProducts();
  };

  return { products, loading, error, pagination, refresh };
};

// Custom hook for product categories
export const useCategories = (cooperativeId = null, page = 1, limit = 10, enablePagination = false) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 0
  });

  // Add a ref to track ongoing requests to prevent duplicates
  const requestRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastRequestParams = useRef(null);

  const fetchCategories = useCallback(async () => {
    // Create a unique key for the current request parameters
    const requestKey = JSON.stringify({ cooperativeId, page, limit, enablePagination });
    
    // Prevent duplicate requests with same parameters
    if (requestRef.current && lastRequestParams.current === requestKey) {
      return;
    }

    try {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      setError(null);
      
      // Mark request as ongoing
      requestRef.current = true;
      lastRequestParams.current = requestKey;
      
      const options = enablePagination ? {
        page,
        limit,
        paginate: true
      } : {};
      
      const response = await ApiService.getCategories(cooperativeId, options);
      
      if (response.status === 200) {
        if (enablePagination && response.data.pagination) {
          // Paginated response
          setCategories(response.data.data || []);
          setPagination(response.data.pagination || {});
        } else {
          // Non-paginated response
          setCategories(response.data || []);
          setPagination({
            current_page: 1,
            per_page: response.data?.length || 0,
            total: response.data?.length || 0,
            last_page: 1
          });
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setCategories([]);
      }
    } finally {
      setLoading(false);
      requestRef.current = null;
    }
  }, [cooperativeId, page, limit, enablePagination]);

  useEffect(() => {
    fetchCategories();
    
    // Cleanup function to cancel ongoing requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      requestRef.current = null;
    };
  }, [fetchCategories]);

  const refresh = useCallback(() => {
    // Force refresh by clearing the last request params
    lastRequestParams.current = null;
    fetchCategories();
  }, [fetchCategories]);

  // Category CRUD operations
  const createCategory = async (categoryData) => {
    try {
      setError(null);
      const response = await ApiService.createCategory(categoryData);
      if (response.status === 200) {
        // Refresh to get the latest data from server
        await fetchCategories();
        return response.data;
      }
      throw new Error(response.message || 'Failed to create category');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateCategory = async (id, categoryData) => {
    try {
      setError(null);
      const response = await ApiService.updateCategory(id, categoryData);
      if (response.status === 200) {
        // Refresh to get the latest data from server
        await fetchCategories();
        return response.data;
      }
      throw new Error(response.message || 'Failed to update category');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteCategory = async (id) => {
    try {
      setError(null);
      const response = await ApiService.deleteCategory(id);
      if (response.status === 200) {
        // Refresh to get the latest data from server
        await fetchCategories();
        return response.data;
      }
      throw new Error(response.message || 'Failed to delete category');
    } catch (err) {
      setError(err.message);
      // Re-fetch categories in case of error to restore correct state
      fetchCategories();
      throw err;
    }
  };

  const generateSlug = async (name) => {
    try {
      const response = await ApiService.generateCategorySlug(name);
      if (response.status === 200) {
        return response.data.slug;
      }
      throw new Error(response.message || 'Failed to generate slug');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return { 
    categories, 
    loading, 
    error, 
    pagination,
    refresh,
    createCategory,
    updateCategory,
    deleteCategory,
    generateSlug
  };
};

// Custom hook for featured products
export const useFeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeaturedProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getFeaturedProducts();
      
      if (response.status === 200) {
        setProducts(response.data || []);
      }
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  const refresh = () => {
    fetchFeaturedProducts();
  };

  return { products, loading, error, refresh };
};

// Custom hook for products on sale
export const useProductsOnSale = (cooperativeId = null, page = 1, limit = 10) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchProductsOnSale = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = cooperativeId 
        ? await ApiService.getProductsOnSaleByCooperative(cooperativeId)
        : await ApiService.getProductsOnSale(page, limit);
      
      if (response.status === 200) {
        if (cooperativeId) {
          setProducts(response.data || []);
        } else {
          setProducts(response.data.products || []);
          setPagination(response.data.pagination || {});
        }
      }
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [cooperativeId, page, limit]);

  useEffect(() => {
    fetchProductsOnSale();
  }, [fetchProductsOnSale]);

  const refresh = () => {
    fetchProductsOnSale();
  };

  return { products, loading, error, pagination, refresh };
};

// Custom hook for single product
export const useProduct = (productId) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getProduct(productId);
      
      if (response.status === 200) {
        setProduct(response.data);
      }
    } catch (err) {
      setError(err.message);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const refresh = () => {
    fetchProduct();
  };

  return { product, loading, error, refresh };
};

// Custom hook for user profile (to get cooperative info)
// DEPRECATED: Use useProfile from ProfileContext instead for better performance
export const useUserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    // Try to get from localStorage first to avoid unnecessary API calls
    const cachedProfile = localStorage.getItem('user_profile');
    if (cachedProfile) {
      try {
        setProfile(JSON.parse(cachedProfile));
        setLoading(false);
        return;
      } catch (e) {
        console.warn('Failed to parse cached profile');
      }
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getProfile();
      
      if (response.status === 200) {
        setProfile(response.data);
        localStorage.setItem('user_profile', JSON.stringify(response.data));
      }
    } catch (err) {
      setError(err.message);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const refresh = () => {
    fetchProfile();
  };

  return { profile, loading, error, refresh };
};

// Custom hook for product statistics
export const useProductStats = (cooperativeId) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!cooperativeId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getProductStatsByCooperative(cooperativeId);
      
      if (response.status === 200) {
        setStats(response.data);
      }
    } catch (err) {
      setError(err.message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [cooperativeId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refresh = () => {
    fetchStats();
  };

  return { stats, loading, error, refresh };
};

// Custom hook for top products
export const useTopProducts = (cooperativeId, sortBy = 'price', limit = 5) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTopProducts = useCallback(async () => {
    if (!cooperativeId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getTopProductsByCooperative(cooperativeId, sortBy, limit);
      
      if (response.status === 200) {
        setProducts(response.data || []);
      }
    } catch (err) {
      setError(err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [cooperativeId, sortBy, limit]);

  useEffect(() => {
    fetchTopProducts();
  }, [fetchTopProducts]);

  const refresh = () => {
    fetchTopProducts();
  };

  return { products, loading, error, refresh };
};

// Generic API hook for custom requests
export const useApi = () => {
  const api = useMemo(() => ({
    get: async (endpoint, options = {}) => {
      return ApiService.fetchAPI(endpoint, {
        method: 'GET',
        ...options
      });
    },
    
    post: async (endpoint, data = null, options = {}) => {
      return ApiService.fetchAPI(endpoint, {
        method: 'POST',
        body: data ? JSON.stringify(data) : null,
        ...options
      });
    },
    
    put: async (endpoint, data = null, options = {}) => {
      return ApiService.fetchAPI(endpoint, {
        method: 'PUT',
        body: data ? JSON.stringify(data) : null,
        ...options
      });
    },
    
    delete: async (endpoint, options = {}) => {
      return ApiService.fetchAPI(endpoint, {
        method: 'DELETE',
        ...options
      });
    },
    
    patch: async (endpoint, data = null, options = {}) => {
      return ApiService.fetchAPI(endpoint, {
        method: 'PATCH',
        body: data ? JSON.stringify(data) : null,
        ...options
      });
    }
  }), []);

  return api;
};

// Custom hook for members management
export const useMembers = (params = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestRef = useRef(null);
  const getProfile = useUserProfile()
  const cooperativeId = getProfile?.profile?.cooperative?.id

  // Memoize the fetch function to prevent infinite re-renders
  const fetchMembers = useCallback(async () => {
    if (!cooperativeId) {
      setLoading(false); 
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      // Cancel previous request if it exists
      if (requestRef.current) {
        requestRef.current.abort();
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      requestRef.current = abortController;
      
      const queryParams = new URLSearchParams();
      
      if (params.search) queryParams.append('search', params.search);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      
      const endpoint = `/cooperatives/${cooperativeId}/members${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await ApiService.get(endpoint, { signal: abortController.signal });
      
      if (response.status === 200) {
        setData(response);
      } else {
        throw new Error(response.message || 'Failed to fetch members');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to fetch members');
        setData(null);
      }
    } finally {
      if (!requestRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [params.search, params.status, params.page, params.limit, cooperativeId]);

  useEffect(() => {
    fetchMembers();
    
    // Cleanup function to cancel any pending requests
    return () => {
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchMembers, cooperativeId]);

  const refetch = useCallback(() => {
    fetchMembers();
  }, [fetchMembers]);

  return { data, loading, error, refetch };
};

export const useProspectMembers = (params = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestRef = useRef(null);
  const getProfile = useUserProfile()
  const cooperativeId = getProfile?.profile?.cooperative?.id

  // Memoize the fetch function to prevent infinite re-renders
  const fetchMembers = useCallback(async () => {
     if (!cooperativeId) {
      setLoading(false); 
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      // Cancel previous request if it exists
      if (requestRef.current) {
        requestRef.current.abort();
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      requestRef.current = abortController;
      
      const queryParams = new URLSearchParams();
      
      if (params.search) queryParams.append('search', params.search);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      
      const endpoint = `/cooperatives/${cooperativeId}/prospect-members${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await ApiService.get(endpoint, { signal: abortController.signal });
      
      if (response.status === 200) {
        setData(response);
      } else {
        throw new Error(response.message || 'Failed to fetch members');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to fetch members');
        setData(null);
      }
    } finally {
      if (!requestRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [params.search, params.status, params.page, params.limit, cooperativeId]);

  useEffect(() => {
    fetchMembers();
    
    // Cleanup function to cancel any pending requests
    return () => {
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchMembers, cooperativeId]);

  const refetch = useCallback(() => {
    fetchMembers();
  }, [fetchMembers]);

  return { data, loading, error, refetch };
};

export const useLoanProducts = (params = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestRef = useRef(null);
  const getProfile = useUserProfile()
  const cooperativeId = getProfile?.profile?.cooperative?.id

  // Memoize the fetch function to prevent infinite re-renders
  const fetchLoanProducts = useCallback(async () => {
     if (!cooperativeId) {
      setLoading(false); 
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      // Cancel previous request if it exists
      if (requestRef.current) {
        requestRef.current.abort();
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      requestRef.current = abortController;
      
      const queryParams = new URLSearchParams();
      
      if (params.search) queryParams.append('search', params.search);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      
      const endpoint = `/cooperatives/${cooperativeId}/loan-products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await ApiService.get(endpoint, { signal: abortController.signal });
      
      if (response.status === 200) {
        setData(response);
      } else {
        throw new Error(response.message || 'Failed to fetch members');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to fetch members');
        setData(null);
      }
    } finally {
      if (!requestRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [params.search, params.status, params.page, params.limit, cooperativeId]);

  useEffect(() => {
    fetchLoanProducts();
    
    // Cleanup function to cancel any pending requests
    return () => {
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchLoanProducts, cooperativeId]);

  const refetch = useCallback(() => {
    fetchLoanProducts();
  }, [fetchLoanProducts]);

  return { data, loading, error, refetch };
};
